import React, { useMemo } from 'react';
import { Category, TextComponentFragment } from '@graphql/generated';
import { WebsiteEditorRichTextTelemetryProvider } from './RichText.telemetry';
import { useAddRichTextNavigations } from './hooks/useAddRichTextNavigations';
import { CustomComponentsProps, CustomComponentsWrapperType } from '@website-editor/customComponents';
import { RichTextListGlobalStyle } from './RichText.styles';
import { useCustomComponentStateContext } from '@website-editor/features/custom-page/context/CustomComponentStateContext/CustomComponentState.context';
import { useEventDesignTheming } from '@apps/guest/routes/GuestSite/GuestSite.utils';
import { useFeatureValue } from '@shared/core/featureFlags';
import { createLazyComponentFromNamedExport } from '@shared/utils/createLazyComponent';
import { RichTextEditListItem } from './wrappers/RichTextEditListItem/RichTextEditListItem';

// Lazy load sub-components
const RichTextForm = createLazyComponentFromNamedExport(() => import(/* webpackChunkName: "content/websiteEditorRichTextForm" */ './components/RichTextForm'), 'RichTextForm');

const RichTextAddListItem = createLazyComponentFromNamedExport(
  () => import(/* webpackChunkName: "content/websiteEditorRichTextAddListItem" */ './wrappers/RichTextAddListItem/RichTextAddListItem'),
  'RichTextAddListItem',
);

export interface RichTextProps
  extends CustomComponentsProps,
    Readonly<{
      componentData: TextComponentFragment;
      isLastItem?: boolean;
      isFirstOfType?: boolean;
    }> {}

const RichTextOverrideStyles: React.FC = React.memo(() => {
  const { eventDesign } = useCustomComponentStateContext();
  const { theme } = useEventDesignTheming({ eventDesign });

  const fontPackExperimentEnabled = useFeatureValue('fontPackExperiment').value === 'treatment';

  const fontMap = useMemo(() => {
    if (fontPackExperimentEnabled) {
      return eventDesign?.fonts?.reduce((acc, font) => ({ ...acc, ...{ [font.category]: font.font?.fontFamily } }), {} as Record<Category, string>);
    }
    return;
  }, [eventDesign?.fonts, fontPackExperimentEnabled]);

  return (
    <RichTextListGlobalStyle
      theme={theme}
      overrides={{
        Paragraph: {
          props: { fontFamily: fontMap?.PARAGRAPH },
        },
        Heading: {
          props: { fontFamily: fontMap?.HEADING },
        },
      }}
    />
  );
});

export const RichText: React.FC<RichTextProps> = props => {
  const { wrapperType, componentData, isFirstOfType, ...restProps } = props;
  useAddRichTextNavigations(props);

  const getRenderComponent = useMemo(() => {
    switch (wrapperType) {
      case CustomComponentsWrapperType.AddComponentList:
        return (
          <RichTextAddListItem
            {...restProps}
            componentData={componentData}
          />
        );
      case CustomComponentsWrapperType.EditComponentList:
        return (
          <RichTextEditListItem
            {...restProps}
            componentData={componentData}
          />
        );
      default:
        return (
          <RichTextForm
            {...restProps}
            richTextData={componentData}
            isEditMode
            isSingleComponent
          />
        );
    }
  }, [componentData, restProps, wrapperType]);

  return (
    <WebsiteEditorRichTextTelemetryProvider>
      {isFirstOfType && <RichTextOverrideStyles />}
      {getRenderComponent}
    </WebsiteEditorRichTextTelemetryProvider>
  );
};
